import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { queryDateFormat } from "../../config/constants";
import { PUBLIC_PATHS, useAuth } from "../context/AuthContext";
import { handleErrors } from "../services/handleErrors";
import useQueryAsState from "./useQueryAsState";

const usePaging = (serviceFetcher) => {
  const { refreshToken } = useAuth();
  // Rest status
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [statusStats, _setStatusStats] = useState(null);
  const [timeStats, _setTimeStats] = useState(null);
  const [cobertura, _setCobertura] = useState(null);
  const refStatus = useRef(statusStats);
  const refCobertura = useRef(cobertura);
  const setStatusStats = (newStatusStats) => {
    refStatus.current = newStatusStats;
    _setStatusStats(newStatusStats);
  };

  const setCobertura = (newCobertura) => {
    refCobertura.current = newCobertura;
    _setCobertura(newCobertura);
  };

  // location access
  const [query, updateQuery, history, isQueryChanged] = useQueryAsState();

  query.startDate = query.startDate ? moment(query.startDate, queryDateFormat).toDate() : null;
  query.endDate = query.endDate ? moment(query.endDate, queryDateFormat).toDate() : null;

  /**
   * Function for load and reload paging data
   */
  const reloadData = async (location) => {
    if (PUBLIC_PATHS.includes(location.pathname)) {
      return;
    }
    setIsLoading(true);
    try {
      const result = await serviceFetcher(location.search, refStatus.current === null);
      if (result) {
        setData(result.result.rows);
        setTotalRows(result.result.count);
        if (result.result.cobertura) {
          setCobertura(result.result.cobertura);
        }
        if (result.result.statusStats) {
          setStatusStats(result.result.statusStats);
        }
        if (result.result.timeStats) {
          _setTimeStats(result.result.timeStats);
        }
      }
    } catch (err) {
      setError(err);
      handleErrors(err, toast.error, refreshToken);
    }
    setIsLoading(false);
  };

  /**
   * First load data and history listener
   */
  useEffect(() => {
    reloadData(history.location);
    const unregisterListen = history.listen(reloadData);
    return () => {
      unregisterListen();
    };
  }, []);

  const setFilterValue = (values) => {
    const filterChanges = {};
    Object.keys(values).forEach((key) => {
      const value = values[key];
      if (["startDate", "endDate"].includes(key) && value !== null) {
        filterChanges[key] = moment(value).format(queryDateFormat);
      } else {
        filterChanges[key] = value;
      }
    });
    if (Object.keys(filterChanges).length > 0) {
      if (isQueryChanged(filterChanges)) {
        filterChanges.page = 1;
        updateQuery(filterChanges);
      }
    }
  };

  const handleSortsChange = (column, sortDirection) => {
    const signDirection = sortDirection === "asc" ? "-" : "";
    const newSort = `${signDirection}${column.id}`;
    updateQuery({ sort: newSort, page: 1 });
  };

  const handlePageChange = (page) => {
    updateQuery({ page });
  };

  const handleRecordsPerPageChange = (perpage, page) => {
    updateQuery({ page, perpage });
  };

  return {
    query,
    setFilterValue,
    handleSortsChange,
    handlePageChange,
    isLoading,
    error,
    totalRows,
    data,
    statusStats,
    timeStats,
    coberturaStats: cobertura,
    handleRecordsPerPageChange,
    reloadData,
  };
};

export default usePaging;
